// ── src/coverageDollars.js ───────────────────────────────────────────────────
// A COVERAGE THE AIP HAS REPORTED ACRES FOR AND NO DOLLARS ON.
//
// Every guarantee surface in this app is LIABILITY-FIRST: `liability ÷ price` is
// RMA's own acre-weighted Marketable Guarantee, the figure the SOI prints, and
// `APH × coverage × acres × price` is the fallback for a line RMA has not keyed
// liability on yet. That fallback is correct and must stay. What was missing is
// that NOTHING SAID WHICH ONE RAN — so a coverage carrying no carrier dollars at
// all rendered identically to a fully-priced one, and the only difference was a
// figure that happened to be close.
//
// ED BERWANGER, MP-0939954 CY2026, is the case that produced this file. Measured
// against the LIVE R&H file 2026-09-13:
//
//   P11  cov 750411385  npac="U"  acres=46  liability=""  premium=""  subsidy=""
//   P14  cov 750411384  plan 88 (ECO-RP)  associated=750411385  — and ZERO P11 rows
//
// So R&H currently reports 46 non-premium acres and no dollars whatsoever, while
// Ed's own Schedule of Insurance prints 46.00 planted acres, $23,760 liability and
// $927 premium, plus an ECO-RP at $2,851 / $1,470. His card showed $23,749.11 —
// `149 × 0.75 × 46 × $4.62`, this app's own model, within $11 of the statement by
// arithmetic rather than because anything was read. Nothing on screen said so.
//
// ⚠ THE MISSING DOLLARS ARE NOT RECOVERABLE AND MUST NOT BE INVENTED. The $2,851
// that sits in our raw mirror for his ECO was LAST SEEN IN THE FILE ON 2026-08-18
// — the mirror never deletes, and the mirror has run on 21 separate days since
// without that record reappearing. Deriving $23,760 from the grower's PDF is the
// invented-explanation failure rule zero opens with. The fix is a question to the
// AIP; this file exists so the question is ASKABLE instead of invisible.
//
// ⚠ AND "NO DOLLARS" IS NOT THE SAME CLAIM AS "SOMETHING IS WRONG". Acreage is
// reported before RMA keys liability, so in spring this state is the NORMAL one
// for a large part of the book. The wording therefore names both possibilities and
// picks neither. Measured 2026-09-13, late in the crop year: 4 of 784 CY2026 base
// coverages carrying acres are in it — Berwanger, Craig Rice, J Rice and Clark
// Farms — and three of those four move to `ui_acres` on the next sync, leaving his
// as the only one. In April the same rule will legitimately name hundreds.
//
// ONE RULE, NOT ONE PER SURFACE. The liability-first branch is written out at ten
// call sites in App.jsx; a tenth copy of the question "which branch ran" is how
// the three news handlers drifted twice and how two guarantee engines came to
// disagree about one farm's forward-sell ceiling.

// A guarantee on screen came from exactly one of these.
export const GUAR_FROM_LIABILITY = "rma_liability";  // the AIP's own figure
export const GUAR_FROM_MODEL     = "modelled";       // APH × coverage × acres × price
export const GUAR_NONE           = "none";           // nothing renders

// Insured acres on file. `null`/absent is NOT zero — it means acreage has not been
// reported, which is a different fact from "no acres". Overlays (SCO/ECO) carry
// null acres by construction and are therefore out of scope for every rule here.
export function reportedAcres(r) {
  const a = Number(r && r.acres);
  return Number.isFinite(a) && a > 0 ? a : null;
}

// The AIP stated a liability. A stored ZERO is treated as "not stated" on purpose:
// the syncs write `parseFloat(f[33]) || 0`, so an empty field and a real zero are
// already indistinguishable by the time a row reaches a screen, and RMA does not
// write a $0 liability against acres it insures.
export function carrierLiability(r) {
  const v = Number(r && r.liability);
  return Number.isFinite(v) && v > 0 ? v : null;
}

// Gross premium as the AIP stated it. Same reasoning as above.
export function carrierPremium(r) {
  const v = Number(r && r.totalPremium != null ? r.totalPremium : r && r.total_premium);
  return Number.isFinite(v) && v > 0 ? v : null;
}

// Which branch produced the figure on screen. This mirrors the shipped render
// logic — liability first, then the APH model, then nothing — and exists so a
// surface can REPORT its own branch rather than each one re-deriving the test.
export function guaranteeBasis(r) {
  if (carrierLiability(r) != null) return GUAR_FROM_LIABILITY;
  const aph = Number(r && r.approvedYield);
  const lvl = Number(r && r.coverageLevel);
  if (reportedAcres(r) != null && Number.isFinite(aph) && aph > 0 && Number.isFinite(lvl) && lvl > 0)
    return GUAR_FROM_MODEL;
  return GUAR_NONE;
}

// ⚠ BOTH HALVES, DELIBERATELY. A coverage carrying premium but no liability, or
// liability but no premium, is a different and rarer shape and is NOT this one —
// naming it here would put a caveat on coverages whose dollars are merely partial.
// Silence means the carrier has said nothing about the money at all.
export function isSilentCoverage(r) {
  return reportedAcres(r) != null && carrierLiability(r) == null && carrierPremium(r) == null;
}

export function silentCoverages(rows) {
  return (rows || []).filter(isSilentCoverage);
}

// The sentence the card prints. It names what is missing, says the guarantee is
// ours rather than RMA's where one renders, and gives BOTH explanations without
// choosing — "not yet keyed" and "withdrawn" look identical from the dollars, and
// only the AIP can say which. It never says a number is wrong.
export function silentCoverageNote(rows) {
  const hit = silentCoverages(rows);
  if (!hit.length) return null;
  const modelled = hit.filter((r) => guaranteeBasis(r) === GUAR_FROM_MODEL).length;
  const acres = hit.reduce((s, r) => s + (reportedAcres(r) || 0), 0);
  const cells = [...new Set(hit.map((r) => `${r.county || "?"} ${r.crop || "?"}`))];
  return {
    count: hit.length,
    acres: +acres.toFixed(1),
    modelled,
    cells,
    headline: `The carrier has reported no liability and no premium on ` +
      `${hit.length} coverage${hit.length > 1 ? "s" : ""} (${cells.join(", ")}), ` +
      `covering ${acres.toFixed(1)} reported acres.`,
    detail: (modelled > 0
      ? `The guarantee shown for ${modelled === hit.length ? (modelled > 1 ? "those" : "it") : `${modelled} of them`} ` +
        `is SILAS's own estimate — approved yield × coverage level × acres × price — not RMA's liability. `
      : ``) +
      `Acreage is normally reported before RMA keys the liability, so this can simply mean ` +
      `not priced yet; it can also mean the coverage was withdrawn. The file does not say which. ` +
      `Confirm against the grower's Schedule of Insurance, and with the AIP if they disagree.`,
  };
}
